/**
 * Facts about a rejected API request that more than one policy needs.
 *
 * The `*Outcome.js` modules each answer a different question — is this delete
 * already done, is this collection filter dead, is this save recoverable — but
 * they were each re-deriving the same underlying fact about the error, with
 * their own copy of the reasoning. One copy drifting from the other is
 * invisible, because each module has its own passing unit tests.
 *
 * Kept dependency-free and side-effect-free at import time so the policies that
 * build on it stay unit-testable without a network or a Vue app, and so
 * `api.js` can import them without a cycle.
 */

/**
 * Did something other than our API produce this response?
 *
 * `makeAuthenticatedRequest` decides this per attempt (`isForeignErrorBody`)
 * and tags the thrown error. A status invented by an intercepting proxy or a
 * captive portal never reached the origin, so it says NOTHING about the state
 * of the resource — the request's outcome is unknown, not known-and-negative.
 *
 * Every policy that turns a status into a claim about server state has to
 * refuse these: a proxy's 404 is not "the row is gone" (that would report a
 * deletion that may not have happened) and it is not "the collection is gone"
 * (that would drop a perfectly good filter on a network fault).
 *
 * LINKFLARE-12 / 142796468 is the case that made this real: a Tomcat 6.0.37
 * error page returned for a route that cannot produce that status, with no
 * server span for it at all.
 *
 * @param {Error} error — whatever the request rejected with
 * @returns {boolean}
 */
export function isForeignError(error) {
  return error?.foreignResponse === true
}
